/*
 * Centralised link-target rule engine.
 *
 * One backend-configurable rule decides whether a link opens in a new tab or
 * the same tab, based on where the link points:
 *
 *   Any host outside ours (incl. external blogs)  new tab   (target="_blank" + rel)
 *   Our own blog / article                        same tab  (target="_self")
 *   Product                                       same tab  (target="_self")
 *   Collection                                    same tab  (target="_self")
 *   Any other page on our own domain              same tab  (target="_self")
 *
 * So: leaving the store opens a new tab, staying on it does not. The page type
 * does not change the outcome; the per-type rules exist only so one type can be
 * overridden from theme settings without touching code.
 *
 * This runs in JS rather than Liquid because most links are not authored in the
 * theme: they come from rich-text metafields, page/article bodies, nav menus and
 * app-rendered markup (Boost grids, quick-view, cart drawer). A DOM-level rule is
 * the only place that sees all of them. Behaviour is driven entirely by
 * window.iwLinkTarget, which snippets/iw-link-target.liquid renders from theme
 * settings — so merchants change the rule in the admin, not in code.
 *
 * Deliberately conservative: checkout, cart, account and payment-provider links
 * are left exactly as the author wrote them, and any link can opt out with
 * data-iw-link-target="skip".
 */
(function () {
  'use strict';

  if (window.iwLinkTargetInit) return;
  window.iwLinkTargetInit = true;

  var config = window.iwLinkTarget || {};
  if (config.enabled === false) return;

  // Rule values. Any other value (e.g. 'ignore') leaves the link as authored.
  var NEW_TAB = 'new';
  var SAME_TAB = 'same';

  var rules = {
    external: config.external || NEW_TAB,
    blog: config.blog || SAME_TAB,
    product: config.product || SAME_TAB,
    collection: config.collection || SAME_TAB,
    page: config.page || SAME_TAB
  };

  var externalRel = typeof config.rel === 'string' ? config.rel : 'noopener noreferrer';

  // Hosts that count as "us". Seeded from Liquid with request.host, the primary
  // domain and the .myshopify.com domain; the current host is always included so
  // preview/alternate domains behave the same as production.
  var internalHosts = hostSet(toList(config.internalHosts).concat(toList(config.extraInternalHosts)));
  internalHosts[normalizeHost(window.location.hostname)] = true;

  // Hosts we never touch. These are checkout / account / payment / app-portal
  // surfaces where forcing a new tab would strand the customer in a detached
  // session, plus the merchant's own additions from theme settings.
  var ignoreHosts = hostSet(
    [
      'shopify.com',
      'myshopify.com',
      'shopifycdn.com',
      'shopifycloud.com',
      'shop.app',
      'shopifypay.com',
      'paypal.com',
      'rechargeapps.com',
      'rechargepayments.com'
    ].concat(toList(config.ignoreHosts))
  );

  // Internal routes that are part of a purchase/session flow, or are app proxies
  // whose paths we can't reason about. Matched on the first path segment.
  var IGNORE_ROUTES = {
    cart: true,
    checkout: true,
    checkouts: true,
    account: true,
    customer_authentication: true,
    customer_identity: true,
    apps: true,
    tools: true,
    services: true,
    wpm: true
  };

  var SAFE_TARGETS = { '': true, _blank: true, _self: true, _top: true, _parent: true };

  // Own-property lookup, so a path segment or target named after an Object
  // prototype member ("constructor", "toString") can't match by accident.
  function has(map, key) {
    return Object.prototype.hasOwnProperty.call(map, key);
  }

  function normalizeHost(host) {
    return String(host || '')
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/\/.*$/, '')
      .replace(/:\d+$/, '')
      .replace(/^www\./, '');
  }

  // Domain settings arrive either as a Liquid-built array or as a merchant-typed
  // textarea (newline, comma or space separated).
  function toList(value) {
    if (Object.prototype.toString.call(value) === '[object Array]') return value;
    if (typeof value === 'string') return value.split(/[\s,;]+/);
    return [];
  }

  function hostSet(list) {
    var set = {};
    (list || []).forEach(function (host) {
      var normalized = normalizeHost(host);
      if (normalized) set[normalized] = true;
    });
    return set;
  }

  // True when host is in set, or is a subdomain of an entry in it — so listing
  // "rechargeapps.com" also covers "<store>.rechargeapps.com". Used for the
  // ignore list only; internal hosts match exactly (see classify).
  function hostMatches(host, set) {
    if (has(set, host)) return true;
    for (var entry in set) {
      if (has(set, entry) && host.length > entry.length) {
        if (host.slice(-(entry.length + 1)) === '.' + entry) return true;
      }
    }
    return false;
  }

  function parseUrl(href) {
    try {
      return new URL(href, window.location.href);
    } catch (e) {
      return null;
    }
  }

  /*
   * Classify an internal path. Route segments are matched positionally rather
   * than by prefix so that locale/market prefixes ("/en-ca/products/x") and
   * nested product URLs ("/collections/foo/products/bar") land correctly — and
   * so that a page handle like "/pages/our-products" is not mistaken for a PDP.
   *
   * Product wins over collection, because /collections/<c>/products/<p> renders
   * the product page.
   */
  function classifyPath(pathname) {
    var parts = pathname.split('/').filter(Boolean);
    // "/" — the homepage counts as a plain internal page.
    if (!parts.length) return 'page';

    for (var i = 0; i < parts.length; i++) {
      if (has(IGNORE_ROUTES, parts[i])) return null;
    }

    var productIndex = parts.indexOf('products');
    if (productIndex > -1 && parts.length > productIndex + 1) return 'product';

    var blogIndex = parts.indexOf('blogs');
    if (blogIndex > -1 && parts.length > blogIndex + 1) return 'blog';

    var collectionIndex = parts.indexOf('collections');
    if (collectionIndex > -1) return 'collection';

    return 'page';
  }

  // Returns the rule key for an anchor, or null when the link must be left alone.
  function classify(anchor) {
    if (anchor.getAttribute('data-iw-link-target') === 'skip') return null;
    if (anchor.hasAttribute('download')) return null;

    var href = anchor.getAttribute('href');
    if (!href) return null;

    var trimmed = href.trim();
    // In-page anchors and non-navigating schemes (mailto:, tel:, javascript:, …).
    if (!trimmed || trimmed.charAt(0) === '#') return null;

    // A named target means the link drives an iframe — never override it.
    var currentTarget = (anchor.getAttribute('target') || '').trim().toLowerCase();
    if (!has(SAFE_TARGETS, currentTarget)) return null;

    var url = parseUrl(trimmed);
    if (!url) return null;
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;

    // Internal is decided first, so that previewing the theme on the
    // .myshopify.com domain still applies the rule to its own relative links —
    // the ignore list only guards links that leave the storefront.
    //
    // Exact host match, not a subdomain match: the storefront only ever lives on
    // these hosts, so a sibling subdomain like study.<domain>.com is a separate
    // site and counts as external. Add such a host to "Extra domains treated as
    // internal" if it should be kept in the same tab.
    var host = normalizeHost(url.hostname);
    if (has(internalHosts, host)) return classifyPath(url.pathname);
    if (hostMatches(host, ignoreHosts)) return null;

    return 'external';
  }

  function mergeRel(anchor) {
    if (!externalRel) return;

    var existing = (anchor.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
    var seen = {};
    existing.forEach(function (token) {
      seen[token.toLowerCase()] = true;
    });

    // Keep author-supplied tokens (nofollow, sponsored, ugc, me) intact.
    externalRel.split(/\s+/).forEach(function (token) {
      if (token && !seen[token.toLowerCase()]) {
        seen[token.toLowerCase()] = true;
        existing.push(token);
      }
    });

    anchor.setAttribute('rel', existing.join(' '));
  }

  function state(anchor) {
    return (anchor.getAttribute('href') || '') + '\n' + (anchor.getAttribute('target') || '');
  }

  function apply(anchor) {
    // Memoised on href *and* target rather than href alone. App-rendered grids
    // mutate constantly, so re-running must be cheap — but keying on the target
    // too means the rule re-asserts itself if another script overwrites it after
    // our pass, while our own writes still short-circuit so the observer that
    // sees them can't loop.
    if (anchor.iwLinkTargetState === state(anchor)) return;

    var key = classify(anchor);
    var rule = key ? rules[key] : null;

    if (rule === NEW_TAB) {
      anchor.setAttribute('target', '_blank');
      mergeRel(anchor);
    } else if (rule === SAME_TAB) {
      anchor.setAttribute('target', '_self');
    }

    anchor.iwLinkTargetState = state(anchor);
  }

  function scan(root) {
    if (!root || root.nodeType !== 1) return;
    if (root.tagName === 'A') apply(root);

    var anchors = root.querySelectorAll ? root.querySelectorAll('a[href]') : [];
    for (var i = 0; i < anchors.length; i++) apply(anchors[i]);
  }

  var pending = [];
  var scheduled = false;

  function flush() {
    scheduled = false;
    var queue = pending;
    pending = [];
    for (var i = 0; i < queue.length; i++) scan(queue[i]);
  }

  function schedule(node) {
    pending.push(node);
    if (scheduled) return;
    scheduled = true;

    // rAF batches the work with the next paint, but frames are starved in
    // background tabs — the timeout guarantees the queue still drains there, so
    // dynamically injected links are never left unprocessed. Whichever fires
    // first wins; flush() is idempotent, so a second call is a no-op.
    if (window.requestAnimationFrame) window.requestAnimationFrame(flush);
    setTimeout(flush, 50);
  }

  function watch() {
    if (!window.MutationObserver) return;

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];

        if (mutation.type === 'attributes') {
          schedule(mutation.target);
          continue;
        }

        for (var j = 0; j < mutation.addedNodes.length; j++) {
          var node = mutation.addedNodes[j];
          if (node.nodeType === 1) schedule(node);
        }
      }
    }).observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href', 'target']
    });
  }

  function init() {
    scan(document.body || document.documentElement);
    watch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Exposed so app callbacks that inject markup outside the observer's reach can
  // re-apply the rule on demand.
  window.iwApplyLinkTargets = scan;
})();
